import { isNil } from 'lodash';
import React from 'react';
import './BuySteps.scss';
import FundList from '../FundList';
import Review from '../Review';
import FundingSource from '../../../FundingSource/FundingSource/FundingSource';
import ProgressStep from '../../../ProgressStep/ProgressStep';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  CONTRIBUTION_WINDOW_CLOSED_MODAL,
} from '../../../../redux/modules/modals/modals.module';

import { BUY_VANITY_HEADERS } from '../../../../settings/settings';
import AppTitle from '../../../Shared/AppTitle/AppTitle';
import { scrollElementIntoFullView } from '../../../../utils/dom-utils';
import {
  generateTradeDatePurchase,
  getPaperless,
} from '../../../../utils/submission/submission-utility';
import { coalesce } from '../../../../utils/utils';
import If from '../../../Shared/If/If';
import { useDispatch, useSelector } from 'react-redux';
import { selectFund } from '../../../../redux/modules/shoppingCart/shoppingCart.module';
import { getUserBPID } from '../../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import { isFundListNextButtonDisabled } from '../../../../utils/buy/buy-steps-utils';
import { moduleActions } from '../../../../redux/modules';

const BuySteps = ({
  isIFrame,
  getUserNewNasuFunds,
  isSalesforceRep,
  customerWebViewEnabled,
  actions,
  availableFunds,
  banksLoading,
  currentStep,
  disableSubmitButton,
  dispatchPageLoad,
  displayTradeDate,
  editModeBank,
  eligibleBanks,
  error,
  flattenedPurchasingFunds,
  isLoaded,
  isSingleBankWithDisabledStatus,
  openModal,
  nasuAddedNewFunds,
  paperlessSetting,
  personalData,
  purchasingFunds,
  selectedBank,
  setProgress,
  shoppingCartTotal,
  stopTransfer,
  submitButtonDisabled,
  userFunds,
  // eslint-disable-next-line
  vanityName,
}) => {
  const dispatch = useDispatch();
  const userBPID = useSelector(getUserBPID);
  const { eligibleTradeDatesLoading } = useSelector(
    (state) => state.executePurchase
  );
  function handleSubmitTradeDates() {
    const { paperless, updatedPaperless } = paperlessSetting;
    const oldPaperless = getPaperless(paperless, 'MUTUAL_FUND');
    const payload = generateTradeDatePurchase(
      updatedPaperless,
      oldPaperless,
      purchasingFunds,
      selectedBank
    );
    Promise.all([
      dispatch(moduleActions.getEligibleTradeDateAgreementsList(payload)),
    ]).then(() => {
      onClickScroll(3, 'review-step');
    });
  }
  const fundingSourceStepClass = 'funding-source-step';
  function onBuySubmit() {
    const { getContributionWindow, submitPurchase, resetTransaction } = actions;
    const { paperless, updatedPaperless } = paperlessSetting;
    const { primaryAddress } = personalData;
    const { state = null } = primaryAddress || {};
    disableSubmitButton();
    const oldPaperless = getPaperless(paperless, 'MUTUAL_FUND');
    const doSubmit = () =>
      submitPurchase(
        availableFunds,
        updatedPaperless,
        oldPaperless,
        purchasingFunds,
        selectedBank
      );
    scrollElementIntoFullView('buy-page-container');
    if (purchasingFunds.some((fund) => fund.priorYearAmount > 0)) {
      //
      // we have prior year contributions so we need to double check again that we didn't cross
      // the prior year threshold as the client was filling out the buy form.
      //
      getContributionWindow(state).then(
        (data) =>
          data.action.payload.body.withinPriorYearContributionPeriod
            ? doSubmit()
            : openModal(CONTRIBUTION_WINDOW_CLOSED_MODAL),
        //
        // Discussion with business concludes that if the call to re-check the contribution
        // window fails, go ahead and do the submit
        //
        doSubmit
      );
    } else {
      doSubmit();
    }
    resetTransaction();
  }

  function onClickScroll(step, element) {
    setProgress(step);
    scrollElementIntoFullView(element, 15);
  }
  return (
    <form onSubmit={(e) => e.preventDefault()} className="animated fadeInUp">
      <If test={!(isIFrame || (!customerWebViewEnabled && isSalesforceRep))}>
        <AppTitle
          title={coalesce(BUY_VANITY_HEADERS[vanityName], 'Buy Mutual Funds')}
          subtitle="Our three-step process makes it easy to add to your existing portfolio or to invest in new funds."
          mboxTitle="ret-aa-new-mf-buy-title"
          mboxSubtitle="ret-aa-new-mf-buy-subtitle"
        />
      </If>

      <ProgressStep
        title={
          <span>
            Choose <span className="buy-steps-expanded-title"> your</span>{' '}
            funding source
          </span>
        }
        className={fundingSourceStepClass}
        clickable={currentStep > 1}
        step={1}
        active={currentStep === 1}
        complete={currentStep > 1}
        OnClickHeader={() => {
          currentStep > 1 && onClickScroll(1, fundingSourceStepClass);
        }}
        expanded
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Select Funds & Amounts
          </span>
        }
        nextButtonDisabled={
          isNil(selectedBank) || isSingleBankWithDisabledStatus
        }
        onNextClick={() => {
          if (nasuAddedNewFunds) {
            getUserNewNasuFunds.forEach((fund) => {
              dispatch(selectFund(fund.agreementID, fund, userBPID));
            });
          }
          onClickScroll(2, fundingSourceStepClass);
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        <FundingSource
          isSalesforceRep={isSalesforceRep}
          customerWebViewEnabled={customerWebViewEnabled}
          isLoading={banksLoading}
          selectedBank={selectedBank}
          dispatchPageLoad={dispatchPageLoad}
          actions={actions}
          editModeBank={editModeBank}
          fundingSource={eligibleBanks}
          error={error}
          active={currentStep === 1}
          completed={currentStep > 1}
          openDrawer={() => {
            onClickScroll(1, fundingSourceStepClass);
          }}
          isLoaded={isLoaded}
          stopTransfer={stopTransfer}
        />
      </ProgressStep>

      <ProgressStep
        title={
          <span>
            Select your fund(s)
            <span className="buy-steps-expanded-title">
              {' '}
              and configure investment options
            </span>
          </span>
        }
        className="fund-list-step"
        clickable={
          currentStep > 2 ||
          (currentStep !== 2 && !isFundListNextButtonDisabled(purchasingFunds))
        }
        nextButtonLoading={eligibleTradeDatesLoading}
        step={2}
        active={currentStep === 2}
        complete={currentStep > 2}
        OnClickHeader={() => {
          (currentStep > 2 ||
            (currentStep !== 2 &&
              !isFundListNextButtonDisabled(purchasingFunds))) &&
            onClickScroll(2, fundingSourceStepClass);
        }}
        expanded
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonLabel={
          <span>
            <b> NEXT:</b> Review & Submit
          </span>
        }
        nextButtonDisabled={isFundListNextButtonDisabled(purchasingFunds)}
        onNextClick={() => {
          handleSubmitTradeDates();
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        <FundList
          clickable={!isFundListNextButtonDisabled(purchasingFunds)}
          displayTradeDate={displayTradeDate}
        />
      </ProgressStep>

      <ProgressStep
        title={
          <span>
            Review <span className="visible-xs-inline">& </span>
            <span className="hidden-xs">and </span>submit{' '}
            <span className="buy-steps-expanded-title"> your trade</span>
          </span>
        }
        className="review-step"
        clickable={currentStep > 3}
        step={3}
        active={currentStep === 3}
        OnClickHeader={() => {
          currentStep >= 3 && setProgress(3);
        }}
        expanded={currentStep === 3}
        nextButtonLabel="SUBMIT"
        nextButtonDisabled={submitButtonDisabled}
        onNextClick={onBuySubmit}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        <Review
          displayTradeDate={displayTradeDate}
          dispatchPageLoad={dispatchPageLoad}
          flattenedPurchasingFunds={flattenedPurchasingFunds}
          userFunds={userFunds}
          selectedBank={selectedBank}
          actions={actions}
          shoppingCartTotal={shoppingCartTotal}
          paperlessSetting={paperlessSetting}
        />
      </ProgressStep>
    </form>
  );
};

export default BuySteps;
